import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, PencilLine } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { sanitizeHtml } from '../utils/html';

export interface WhyWeProps {
  title?: string;
  bodyHtml?: string;
  draftHtml?: string;
  onDraftHtmlChange?: (html: string) => void;
  onCancelDraft?: () => void;
  saveCommitId?: number;
}

export default function WhyWe({
  title = 'Why we recommend them for your project',
  bodyHtml,
  draftHtml,
  onDraftHtmlChange,
  onCancelDraft,
  saveCommitId,
}: WhyWeProps) {
  const COLLAPSE_KEY = 'one_pager_section_expanded__why_we';
  const rootRef = React.useRef<HTMLDivElement | null>(null);

  const decodeHtmlEntities = (raw: string) => {
    try {
      const doc = new DOMParser().parseFromString(raw, 'text/html');
      return doc.documentElement.textContent ?? raw;
    } catch {
      return raw;
    }
  };

  const initialHtml = useMemo(() => {
    if (bodyHtml) return bodyHtml;

    return [
      '<p>We recommend this provider for your operational diligence project in specialty manufacturing because they bring the exact mix of industry experience and executional rigor your team outlined. Over the last five years, they have <strong>supported three separate PE-backed manufacturing carve-outs</strong>, each requiring rapid assessment of supply chain resilience, labor productivity, and cost-structure optimization.</p>',
      '<p>&nbsp;</p>',
      '<p>Their team is <strong>led by a former COO of a $400M industrial components company</strong>, giving them a firsthand perspective on the operational challenges facing mid-market manufacturers. In their most recent engagement, they identified $12M in annualized cost savings for a portfolio company within 60 days — findings that were later validated by the acquirer’s board.</p>',
      '<p>&nbsp;</p>',
      '<p>References consistently <strong>highlight their ability to move quickly without sacrificing depth</strong>, delivering analyses that help PE deal teams make confident investment decisions under tight timelines. Based on your project scope, we believe this provider is well-positioned to deliver actionable insights that directly inform your investment thesis.</p>',
    ].join('');
  }, [bodyHtml]);

  const [isEditing, setIsEditing] = useState(false);
  const [editorHtml, setEditorHtml] = useState<string>('');

  const [isExpanded, setIsExpanded] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(COLLAPSE_KEY) !== 'false';
    } catch {
      return true;
    }
  });

  const displayHtml = draftHtml ?? initialHtml;
  const safeHtml = useMemo(() => {
    const raw = displayHtml ?? '';
    const decoded = raw.includes('&lt;') || raw.includes('&gt;') ? decodeHtmlEntities(raw) : raw;
    return sanitizeHtml(decoded);
  }, [displayHtml]);

  const startEditing = () => {
    setEditorHtml(displayHtml);
    setIsEditing(true);
    setIsExpanded(true);
    try {
      sessionStorage.setItem(COLLAPSE_KEY, 'true');
    } catch {
      // ignore
    }
  };

  const cancelEditing = () => {
    onCancelDraft?.();
    setIsEditing(false);
    setEditorHtml('');
  };

  const finishEditing = () => {
    setIsEditing(false);
  };

  useEffect(() => {
    setIsEditing(false);
    setEditorHtml('');
  }, [saveCommitId]);

  const toggleExpanded = () => {
    if (isEditing) return;
    setIsExpanded((prev) => {
      const next = !prev;
      try {
        sessionStorage.setItem(COLLAPSE_KEY, String(next));
      } catch {
        // ignore
      }
      return next;
    });
  };

  return (
    <div ref={rootRef} className="w-full pb-[20px]">
      <div className="flex items-start justify-between">
        <button type="button" className="flex items-start gap-[13px] text-left" onClick={toggleExpanded}>
          <div className="flex size-[24px] items-center justify-center">
            <div className="flex-none">
              {isExpanded ? <ChevronDown size={24} color="#25ADE3" /> : <ChevronRight size={24} color="#25ADE3" />}
            </div>
          </div>

          <p
            className="flex-1 font-['Hanken_Grotesk',sans-serif] text-[24px] font-bold leading-[36px] tracking-[0.75px] text-[#132543]"
            style={{ fontVariationSettings: "'wdth' 100" }}
          >
            <span>{title}</span>
          </p>
        </button>

        {!isEditing && (
          <button
            type="button"
            className="onepager-edit-link inline-flex items-center gap-[6px] text-center font-['Inter',sans-serif] text-[14px] font-medium leading-[20px] tracking-[-0.1504px]"
            onClick={startEditing}
            data-pdf-exclude
          >
            <PencilLine size={16} color="#53BEE8" />
            Edit
          </button>
        )}
      </div>

      <div
        className={
          'relative w-full font-[\'Open_Sans\',sans-serif] text-[16px] font-normal leading-[22.8px] text-[#31394C] overflow-hidden transition-all duration-200 ease-in-out ' +
          (isExpanded ? 'mt-[28px] max-h-[2000px] opacity-100' : 'mt-0 max-h-0 opacity-0')
        }
        style={{ fontVariationSettings: "'wdth' 100" }}
      >
        {!isEditing && (
          <div
            className="ql-snow [&_em]:italic [&_i]:italic"
            style={{ fontVariationSettings: "'wdth' 100" }}
            dangerouslySetInnerHTML={{ __html: `<div class="ql-editor">${safeHtml}</div>` }}
          />
        )}

        {isEditing && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="flex-1" />
              <button
                type="button"
                className="px-3 py-1 rounded bg-[#29446D] text-white"
                onClick={finishEditing}
              >
                Save
              </button>
              <button
                type="button"
                className="px-3 py-1 rounded border border-gray-200 text-[#29446D]"
                onClick={cancelEditing}
              >
                Cancel
              </button>
            </div>

            <div className="snapshot-quill bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
              <ReactQuill
                theme="snow"
                value={editorHtml}
                onChange={(value) => {
                  setEditorHtml(value);
                  onDraftHtmlChange?.(value);
                }}
                modules={{
                  toolbar: [[{ header: [1, 2, false] }], ['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link'], ['clean']],
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
